// src/pages/UserManagement/UserFilters.tsx
// Filters component with search, department, role, status filters

import React, { useState, useEffect } from 'react';
import { Row, Col, Input, Label, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setFilters, clearFilters } from 'slices/userManagement/reducer';
import { Department, Role } from './types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management');

interface UserFiltersProps {
  departments: Department[];
  roles: Role[];
  onCreateUser: () => void;
}

const UserFilters: React.FC<UserFiltersProps> = ({ departments, roles, onCreateUser }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const { filters } = useSelector((state: any) => state.UserManagement);
  
  // Local state for search input (for debounce)
  const [searchValue, setSearchValue] = useState(filters.searchTerm);
  
  // Debounce search input - update Redux after 500ms. Bỏ qua khi searchValue chưa thực sự đổi so với
  // filters.searchTerm hiện tại (VD: lần chạy đầu tiên lúc mount) — nếu không, dispatch setFilters vẫn
  // tạo object filters mới dù giá trị giống hệt, khiến effect load list ở trang cha (phụ thuộc filters)
  // chạy thêm 1 lần nữa ~500ms sau khi mount, gọi API list 2 lần cho cùng 1 lượt vào trang.
  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      dispatch(setFilters({ searchTerm: searchValue }));
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, dispatch]);
  
  // Sync local state with Redux when filters are cleared
  useEffect(() => {
    if (filters.searchTerm === '') {
      setSearchValue('');
    }
  }, [filters.searchTerm]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  
  const handleDepartmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : undefined;
    dispatch(setFilters({ departmentId: value }));
  };
  
  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : undefined;
    dispatch(setFilters({ roleId: value }));
  };
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ status: e.target.value as any }));
  };
  
  const handleClearFilters = () => {
    setSearchValue('');
    dispatch(clearFilters());
  };
  
  // Check if any filter is active
  const hasActiveFilters = 
    filters.searchTerm || 
    filters.departmentId || 
    filters.roleId || 
    (filters.status && filters.status !== 'All');
  
    return (
      <div className="mb-3">
        <Row className="g-3 align-items-end">
          {/* Search Box */}
          <Col md="auto">
            <Label className="form-label">{st('tim_kiem_label')}</Label>
            <div className="position-relative">
              <Input
                type="text"
                placeholder={st('tim_kiem_theo_ten_email_placeholder')}
                value={searchValue}
                onChange={handleSearchChange}
                className="form-control"
                style={{ width: '229px' }}
              />
              {searchValue && (
                <span className="position-absolute top-50 end-0 translate-middle-y me-2">
                  <small className="text-muted">{st('searching_hint')}</small>
                </span>
              )}
            </div>
          </Col>
        
          {/* Department Filter */}
          <Col md="auto">
            <Label className="form-label">{st('phong_ban_label')}</Label>
            <Input
              type="select"
              value={filters.departmentId || ''}
              onChange={handleDepartmentChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="">{st('tat_ca_option')}</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </Input>
          </Col>
        
          {/* Role Filter */}
          <Col md="auto">
            <Label className="form-label">{st('vai_tro_label_2')}</Label>
            <Input
              type="select"
              value={filters.roleId || ''}
              onChange={handleRoleChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="">{st('tat_ca_option')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Input>
          </Col>
        
          {/* Status Filter */}
          <Col md="auto">
            <Label className="form-label">{st('trang_thai_label')}</Label>
            <Input
              type="select"
              value={filters.status || 'All'}
              onChange={handleStatusChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="All">{st('tat_ca_option')}</option>
              <option value="Active">{st('hoat_dong_option')}</option>
              <option value="Locked">{st('da_khoa_option')}</option>
            </Input>
          </Col>
        
                {/* Clear Filters Button */}
                <Col md="auto">
                  <Button
                    color="light"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                  >
                    <i className="ri-close-circle-line me-1"></i>
                    {st('xoa_bo_loc_button')}
                  </Button>
                </Col>
        
                {/* Bên phải: Nút Thêm mới */}
                <Col md="auto" className="ms-auto">
                  {PageActionPermission.canDisplay(location.pathname, "button-add") && (
                    <Button
                      color="primary"
                      data-action-code="button-add"
                      data-action-name="Tạo tài khoản mới"
                      onClick={onCreateUser}
                    >
                      <i className="ri-add-line me-1"></i>
                      {st('tao_tai_khoan_moi_button')}
                    </Button>
                  )}
                </Col>
      </Row>
      
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <Row className="mt-2">
          <Col>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <small className="text-muted">{st('bo_loc_dang_hoat_dong_hint')}</small>
              {filters.searchTerm && (
                <span className="badge bg-primary">
                  {st('tim_kiem_text')}{filters.searchTerm}"
                </span>
              )}
              {filters.departmentId && (
                <span className="badge bg-primary">
                  {st('phong_ban_text')} {departments.find(d => d.id === filters.departmentId)?.name}
                </span>
              )}
              {filters.roleId && (
                <span className="badge bg-primary">
                  {st('vai_tro_text')} {roles.find(r => r.id === filters.roleId)?.name}
                </span>
              )}
              {filters.status && filters.status !== 'All' && (
                <span className="badge bg-primary">
                  {st('trang_thai_text')} {filters.status}
                </span>
              )}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default UserFilters;